import React, { useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import DashboardLayout from "../components/DashboardLayout";
import { api } from "../lib/api";
import { toast } from "sonner";
import { ArrowLeft, MagnifyingGlass, CheckCircle, XCircle, Image as ImageIcon, Clock, Calculator, Receipt } from "@phosphor-icons/react";

function formatDateTime(iso) {
  try { return new Date(iso).toLocaleString(); } catch { return iso; }
}

function formatMoney(n) {
  if (!isFinite(n)) return "0";
  const s = Number(n).toFixed(2);
  const [intPart, dec] = s.split(".");
  const last3 = intPart.slice(-3);
  const rest = intPart.slice(0, -3);
  const restFmt = rest.replace(/\B(?=(\d{2})+(?!\d))/g, ",");
  return (rest ? restFmt + "," + last3 : last3) + "." + dec;
}

export default function RedeemPage() {
  const { id } = useParams();
  const [code, setCode] = useState("");
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [bill, setBill] = useState("");
  const [redeemError, setRedeemError] = useState(null);

  const lookup = async (e) => {
    if (e) e.preventDefault();
    if (!String(code || '').trim()) return;
    setBusy(true);
    setResult(null);
    setBill("");
    setRedeemError(null);
    try {
      const { data } = await api.get(`/redeem/lookup/${String(code || '').trim()}`);
      setResult({ ok: true, ...data });
    } catch (e) {
      setResult({ ok: false, error: "Failed to fetch reward", detail: e.response?.data?.detail || "Could not find this code" });
    } finally { setBusy(false); }
  };

  const redeem = async () => {
    setBusy(true);
    setRedeemError(null);
    try {
      const body = {};
      if (att?.reward_type === "discount" && billNum > 0) {
        body.bill_amount = billNum;
      }
      const { data } = await api.post(`/redeem/${String(code || '').trim()}`, body);
      if (data.ok) {
        toast.success("Reward redeemed!");
        setResult({ ok: true, attempt: data.attempt, outlet_name: result?.outlet_name, discount: data.discount });
      } else {
        setRedeemError(data.message || "Could not redeem this reward");
      }
    } catch (e) {
      setRedeemError(e.response?.data?.detail || "Failed to redeem. Please try again.");
    } finally { setBusy(false); }
  };

  const att = result?.attempt;
  const discount = result?.discount;
  // Screenshot URL: if already absolute (Supabase Storage), use as-is; otherwise prepend backend URL
  const screenshotFullUrl = att?.screenshot_url
    ? (att.screenshot_url.startsWith("http") ? att.screenshot_url : `${process.env.REACT_APP_BACKEND_URL || ""}${att.screenshot_url}`)
    : null;

  const billNum = parseFloat(bill) || 0;
  const calc = useMemo(() => {
    // Use server-calculated discount if available (after redemption)
    if (discount) return { pct: discount.pct, save: discount.discount_amount, final: discount.final_amount };
    if (!att || att.reward_type !== "discount" || !billNum) return null;
    const pct = parseFloat(att.reward_value) || 0;
    const save = +(billNum * pct / 100).toFixed(2);
    const final = +Math.max(0, billNum - save).toFixed(2);
    return { pct, save, final };
  }, [att, billNum, discount]);

  return (
    <DashboardLayout
      title="Redeem a reward"
      actions={
        id ? (
          <Link to={`/outlet/${id}`} className="btn-ghost" data-testid="redeem-back-btn" style={{ padding: "8px 14px", fontSize: 13 }}>
            <ArrowLeft size={16} weight="bold" /> Back
          </Link>
        ) : null
      }
    >
      <div className="max-w-xl">
        <div className="card-pop p-7">
          <p className="text-sm text-[var(--ink-muted)]">Enter the 6-character code shown on the customer's screen.</p>
          <form onSubmit={lookup} className="mt-4 flex gap-3">
            <input
              data-testid="redeem-code-input"
              className="input-pop code-display uppercase"
              maxLength={10}
              autoFocus
              value={code}
              onChange={(e) => setCode(e.target.value.toUpperCase())}
              placeholder="A4F2B1"
            />
            <button data-testid="redeem-lookup-btn" type="submit" disabled={busy} className="btn-primary">
              <MagnifyingGlass size={16} weight="bold" /> Find
            </button>
          </form>

          {result && (
            <div className="mt-6">
              {!result.ok ? (
                <div className="rounded-xl border-[1.5px] border-[var(--ink)] p-5" style={{ background: "#FFE5E2" }} data-testid="redeem-result-error">
                  <div className="flex items-center gap-3 mb-3">
                    <XCircle size={28} weight="fill" color="var(--error)" />
                    <div>
                      <div className="font-bold">{result.error}</div>
                      <div className="text-sm text-[var(--ink-muted)]">{result.detail}</div>
                    </div>
                  </div>
                  <button onClick={lookup} disabled={busy} className="btn-primary text-sm">
                    Retry
                  </button>
                </div>
              ) : (
                <div className="rounded-xl border-[1.5px] border-[var(--ink)] p-5" style={{ background: att.redeemed ? "var(--secondary)" : "var(--pastel-yellow)" }} data-testid="redeem-result-ok">
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <div className="text-xs font-bold uppercase tracking-widest text-[var(--ink-muted)]">Reward</div>
                      <div className="display text-3xl font-black mt-1">{att.reward_label}</div>
                      <div className="text-xs uppercase tracking-widest font-bold text-[var(--ink-muted)] mt-1">{att.reward_type?.replace("_", " ")}</div>
                      <div className="text-sm mt-1 text-[var(--ink-muted)]">{result.outlet_name}</div>
                    </div>
                    <div className="sm:text-right">
                      <div className="text-xs font-bold uppercase tracking-widest text-[var(--ink-muted)]">Code</div>
                      <div className="code-display text-lg mt-1">{att.redemption_code}</div>
                      <div className="text-xs text-[var(--ink-muted)] mt-2 inline-flex items-center gap-1">
                        <Clock size={12} weight="bold" /> {formatDateTime(att.created_at)}
                      </div>
                    </div>
                  </div>

                  <div className="mt-4 flex items-center gap-3 flex-wrap">
                    {att.redeemed ? (
                      <span className="tag" style={{ background: "var(--success)", color: "#fff", borderColor: "var(--ink)" }}>
                        <CheckCircle size={12} weight="fill" /> Redeemed{att.redeemed_at ? ` • ${formatDateTime(att.redeemed_at)}` : ""}
                      </span>
                    ) : (
                      <span className="tag">Pending</span>
                    )}
                  </div>

                  {screenshotFullUrl && (
                    <div className="mt-4" data-testid="redeem-screenshot-block">
                      <div className="text-xs font-bold uppercase tracking-widest text-[var(--ink-muted)] flex items-center gap-1.5">
                        <ImageIcon size={14} weight="bold" /> Review screenshot
                      </div>
                      <a
                        href={screenshotFullUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="block mt-2 rounded-xl border-[1.5px] border-[var(--ink)] p-2 bg-white hover:bg-[var(--bg)] transition-colors"
                        data-testid="redeem-screenshot-link"
                      >
                        <img
                          src={screenshotFullUrl}
                          alt="Customer review screenshot"
                          data-testid="redeem-screenshot-image"
                          className="w-full max-h-96 object-contain rounded-md"
                        />
                        <div className="text-[11px] text-[var(--ink-muted)] mt-1 text-center">Tap to open full size</div>
                      </a>
                    </div>
                  )}

                  {!att.redeemed && (
                    <>
                      <button onClick={redeem} disabled={busy} data-testid="redeem-confirm-btn" className="btn-primary mt-5">
                        <CheckCircle size={16} weight="bold" /> Mark as redeemed
                      </button>
                      {redeemError && (
                        <div className="mt-3 text-sm text-[var(--error)] font-semibold" data-testid="redeem-action-error">
                          {redeemError}
                        </div>
                      )}
                    </>
                  )}
                </div>
              )}

              {/* Bill calculator — owner side, discount only */}
              {att && att.reward_type === "discount" && (
                <div className="mt-5 card-pop p-6" style={{ background: "var(--pastel-blue)" }} data-testid="owner-bill-calculator">
                  <div className="flex items-center gap-2">
                    <Calculator size={20} weight="bold" />
                    <h3 className="display text-lg font-extrabold">Final bill calculator</h3>
                  </div>
                  <p className="text-xs text-[var(--ink-muted)] mt-1">
                    Enter the customer's bill to compute the discounted total.
                  </p>

                  <div className="mt-4">
                    <label className="text-xs font-bold uppercase tracking-widest text-[var(--ink-muted)]">Bill amount</label>
                    <div className="relative mt-1.5">
                      <span className="absolute left-4 top-1/2 -translate-y-1/2 text-lg font-bold text-[var(--ink-muted)]">₹</span>
                      <input
                        data-testid="owner-bill-amount-input"
                        type="number"
                        inputMode="decimal"
                        min="0"
                        step="0.01"
                        className="input-pop pl-9 text-lg font-bold"
                        value={bill}
                        onChange={(e) => setBill(e.target.value)}
                        placeholder="0.00"
                        autoFocus={false}
                      />
                    </div>
                  </div>

                  {calc && (
                    <div className="mt-4 rounded-xl bg-white border-[1.5px] border-[var(--ink)] p-4 space-y-3">
                      <div className="flex justify-between items-center text-sm">
                        <span className="text-[var(--ink-muted)] flex items-center gap-1.5">
                          <Receipt size={14} weight="bold" /> Original bill
                        </span>
                        <span className="font-bold">₹{formatMoney(billNum)}</span>
                      </div>
                      <div className="flex justify-between items-center text-sm">
                        <span className="text-[var(--success)] font-bold">Customer saves ({calc.pct}% off)</span>
                        <span className="font-extrabold text-[var(--success)]" data-testid="owner-bill-save-amount">
                          - ₹{formatMoney(calc.save)}
                        </span>
                      </div>
                      <div className="border-t border-dashed border-[var(--ink)] pt-3 flex justify-between items-center">
                        <span className="display text-base font-extrabold">Charge customer</span>
                        <span className="display text-2xl font-black" style={{ color: "var(--primary)" }} data-testid="owner-bill-final-amount">
                          ₹{formatMoney(calc.final)}
                        </span>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
}
